"use client";

/**
 * Client-side `time_to_first_chip` (TTFC) and `user_to_first_widget_ms` turn
 * timer. Measures the wall-clock duration from a user's turn dispatch (or
 * page mount, for cold-start) to the moment the first interactive widget
 * lands focus in the browser.
 *
 * `turnIndex` is server-authoritative. The registry no longer maintains a
 * local counter; instead each registry entry captures `latestServerTurnIndex`
 * at `markTurnStart` time, and `setLatestServerTurnIndex` (called from
 * `ChatPanel` when the response metadata arrives) stamps the still-pending
 * entry with the authoritative value. This keeps client beacons in the same
 * `(sessionId, turnIndex)` namespace as server-emitted metrics and fixes
 * the Debug Panel "Turn" tab. See
 * `docs/plans/done/2026-04-25-unified-turn-index-source-of-truth.md`.
 *
 * Scope choice: registry state lives at module scope, not in React context.
 * Two reasons: (a) a single production SPA has exactly one chat surface, so
 * there's no multi-provider isolation need; (b) module scope sidesteps
 * StrictMode double-render concerns and the effect-ordering race where a
 * widget's measurement effect fires before the provider's seed effect. The
 * React hook is a thin shim that exposes the module-scope functions.
 *
 * Clock basis: `performance.now()` throughout. Cold-start t0 = 0 (equivalent
 * to navigation start because `performance.timeOrigin` anchors it), user/
 * widget t0 = `performance.now()` at the outgoing-turn seam.
 *
 * Join key: client-generated sequence `"t_<N>"` tracked via a module-scope
 * `pendingTurnKey`. Widgets do not need to thread any id —
 * `markWidgetReady({ widgetType })` reads the pending key internally.
 *
 * Dedup + StrictMode safety: each registry entry has an `emitted` flag; the
 * first `markWidgetReady` sets it, so a second call for the same turn
 * returns `null`. `markAnyWidgetReady` owns the final clear of
 * `pendingTurnKey`.
 *
 * Plan history:
 *  - docs/plans/2026-04-22-wave-0-3-time-to-first-chip.md — original TTFC.
 *  - docs/plans/done/2026-04-25-unified-turn-index-source-of-truth.md — turnIndex
 *    consolidation (this file).
 */

import { createContext, type ReactNode, useContext } from "react";

type TurnTrigger = "user_send" | "cold_start";

/**
 * Widget types eligible to emit `latency:user_to_first_widget_ms`. Broader than
 * the `time_to_first_chip` set — the new metric covers every interactable
 * widget in the chatless flow so perceived-latency cohorts (e.g. trace-mode
 * plan vs disabled) compare across widget-type mixes without normalization.
 */
export type InteractableWidgetType = "chips" | "chip-group" | "text-input";

type RegistryEntry = {
  t0: number;
  trigger: TurnTrigger;
  /**
   * Server-authoritative turnIndex captured at `markTurnStart` time. Updated
   * (stamped) by `stampLatestTurnIndex` once the response metadata for this
   * turn arrives — that's when the value becomes authoritative. Cold-start
   * entries never receive a stamp and stay at -1, which correctly tags them
   * as pre-turn for downstream analytics.
   */
  turnIndexAtStart: number;
  widgetType?: "chips" | "chip-group";
  emitted: boolean;
  /**
   * Independent "first interactable widget" bit. Tracked separately from
   * `emitted` so a chip turn fires BOTH `time_to_first_chip` and
   * `user_to_first_widget_ms` once each, and a text-input turn fires only
   * `user_to_first_widget_ms`. Never reset after first emission per turn.
   */
  anyWidgetEmitted: boolean;
};

export type TurnTimingValue = {
  markTurnStart: (trigger: TurnTrigger) => string;
  markWidgetReady: (opts: { widgetType: "chips" | "chip-group" }) => {
    durationMs: number;
    trigger: TurnTrigger;
    turnIndex: number;
  } | null;
  /**
   * First-interactable-widget boundary for this turn. Broader than
   * `markWidgetReady`: includes `text-input` and can coexist with a chip emit.
   * Returns null on second+ call for the same turn, for a turn with no
   * pending key, or when called from SSR (no performance.now).
   */
  markAnyWidgetReady: (opts: { widgetType: InteractableWidgetType }) => {
    durationMs: number;
    trigger: TurnTrigger;
    turnIndex: number;
    widgetType: InteractableWidgetType;
  } | null;
};

// ---------- Module-scope registry ----------

const registry = new Map<string, RegistryEntry>();
let pendingTurnKey: string | null = null;
/** Local counter for generating unique registry keys (`t_<N>`). Distinct from
 *  any analytics turnIndex — those come from the server-authoritative tracker. */
let keyCounter = 0;
let coldStartSeeded = false;

const TTL_MS = 5 * 60 * 1000;
const TTL_SWEEP_MIN_SIZE = 50;

function nowMs(): number {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

function sweepTtl(): void {
  if (registry.size < TTL_SWEEP_MIN_SIZE) return;
  const cutoff = nowMs() - TTL_MS;
  for (const [k, v] of registry.entries()) {
    if (v.t0 < cutoff) registry.delete(k);
  }
}

export function markTurnStart(trigger: TurnTrigger): string {
  const n = keyCounter;
  keyCounter = n + 1;
  const key = `t_${n}`;
  const t0 = trigger === "cold_start" ? 0 : nowMs();
  // Bootstrap with -1; `stampLatestTurnIndex` will overwrite when the server's
  // metadata frame arrives during the response stream. Cold-start entries
  // never receive a server response (no chat request), so they stay at -1 —
  // correct semantics for "pre-turn".
  registry.set(key, {
    t0,
    trigger,
    turnIndexAtStart: -1,
    emitted: false,
    anyWidgetEmitted: false,
  });
  pendingTurnKey = key;
  sweepTtl();
  return key;
}

/**
 * Stamp the still-pending turn entry with the authoritative server turnIndex.
 * Called from `setLatestServerTurnIndex` (turn-index-tracker.ts) when a chat
 * response's `messageMetadata.turnIndex` is observed (currently the sole
 * transport — cold-start widgets stay at the -1 sentinel and never receive
 * a stamp).
 *
 * Only stamps if the entry is still bootstrap (-1) — guards against a later
 * turn's metadata overwriting an already-stamped earlier entry in the
 * (structurally rare) fast-typist race.
 */
export function stampLatestTurnIndex(value: number): void {
  if (!pendingTurnKey) return;
  const entry = registry.get(pendingTurnKey);
  if (!entry) return;
  if (entry.turnIndexAtStart !== -1) return;
  entry.turnIndexAtStart = value;
}

export function markWidgetReady(opts: { widgetType: "chips" | "chip-group" }): {
  durationMs: number;
  trigger: TurnTrigger;
  turnIndex: number;
} | null {
  const key = pendingTurnKey;
  if (!key) return null;
  const entry = registry.get(key);
  if (!entry || entry.emitted) return null;
  const t1 = nowMs();
  // Negative-gap guard (clock-skew belt + braces — same clock basis shouldn't
  // produce it, but nonnegative matches the schema).
  const durationMs = Math.max(0, t1 - entry.t0);
  entry.emitted = true;
  entry.widgetType = opts.widgetType;
  // Note: do NOT clear pendingTurnKey here — `markAnyWidgetReady` may still
  // fire for the same turn on widget-type-specific renderers that run after
  // chip focus. `markAnyWidgetReady` owns the final clear.
  return {
    durationMs,
    trigger: entry.trigger,
    turnIndex: entry.turnIndexAtStart,
  };
}

/**
 * Boundary for `latency:user_to_first_widget_ms`. Broader than
 * `markWidgetReady`: fires for any interactable widget type (chips,
 * chip-group, text-input) the first time one becomes focused in a turn.
 *
 * Independent from `markWidgetReady`'s dedupe bit (`entry.emitted`) — chip
 * turns emit both metrics; text-input turns emit only this one.
 *
 * Also the final clear-point for `pendingTurnKey` on a turn. `markWidgetReady`
 * intentionally no longer clears so a chip widget can emit TTFC and then this
 * function completes the turn boundary afterwards. On text-input-only turns,
 * `markWidgetReady` never fires and this function handles the clear.
 */
export function markAnyWidgetReady(opts: { widgetType: InteractableWidgetType }): {
  durationMs: number;
  trigger: TurnTrigger;
  turnIndex: number;
  widgetType: InteractableWidgetType;
} | null {
  const key = pendingTurnKey;
  if (!key) return null;
  const entry = registry.get(key);
  if (!entry || entry.anyWidgetEmitted) return null;
  const t1 = nowMs();
  const durationMs = Math.max(0, t1 - entry.t0);
  entry.anyWidgetEmitted = true;
  pendingTurnKey = null;
  return {
    durationMs,
    trigger: entry.trigger,
    turnIndex: entry.turnIndexAtStart,
    widgetType: opts.widgetType,
  };
}

/** Seed the cold-start entry exactly once per module lifetime. Called from
 *  the provider on first mount; idempotent across StrictMode double-mount. */
export function ensureColdStart(): void {
  if (coldStartSeeded) return;
  coldStartSeeded = true;
  markTurnStart("cold_start");
}

/**
 * Reset all registry state. Called by `ChatPanel.resetSession()` on "clear
 * conversation" / site-switch / category-switch so the registry starts clean
 * for the new session. The companion `resetLatestServerTurnIndex` (in
 * turn-index-tracker.ts) resets the server-side counter view; both must be
 * called together on session reset.
 *
 * Also consumed by the test suite via the `_resetTurnTimingForTests` alias.
 */
export function resetTurnTimingRegistry(): void {
  registry.clear();
  pendingTurnKey = null;
  keyCounter = 0;
  coldStartSeeded = false;
}

/** @deprecated alias kept for existing test-call sites. */
export const _resetTurnTimingForTests = resetTurnTimingRegistry;

// ---------- React surface ----------

const TurnTimingContext = createContext<TurnTimingValue>({
  markTurnStart,
  markWidgetReady,
  markAnyWidgetReady,
});

export function TurnTimingProvider({ children }: { children: ReactNode }) {
  ensureColdStart();
  return (
    <TurnTimingContext.Provider value={{ markTurnStart, markWidgetReady, markAnyWidgetReady }}>
      {children}
    </TurnTimingContext.Provider>
  );
}

export function useTurnTiming(): TurnTimingValue {
  return useContext(TurnTimingContext);
}
